import { useEffect, useMemo, useRef, useState } from "react";

import { isTopModal, useModalStack } from "@/lib/modalStack";
import { useExitTransition } from "@/lib/useExitTransition";
import { useFocusTrap } from "@/lib/useFocusTrap";

const MODAL_ID = "command-palette";

export interface CommandAction {
  id: string;
  group: string;
  label: string;
  description?: string;
  shortcut?: string;
  onActivate: () => void;
}

interface Props {
  open: boolean;
  actions: CommandAction[];
  onClose: () => void;
}

function matchScore(action: CommandAction, q: string): number {
  if (!q) return 1;
  const label = action.label.toLowerCase();
  const group = action.group.toLowerCase();
  const desc = action.description?.toLowerCase() ?? "";
  if (label === q) return 100;
  if (label.startsWith(q)) return 80;
  if (label.includes(q)) return 60;
  if (group.includes(q)) return 30;
  if (desc.includes(q)) return 20;
  return 0;
}

export function CommandPalette({ open, actions, onClose }: Props) {
  const [query, setQuery] = useState("");
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const { rendered, closing } = useExitTransition(open);
  useModalStack(MODAL_ID, open);
  useFocusTrap(dialogRef, open);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return actions;
    return actions
      .map((a) => ({ action: a, score: matchScore(a, q) }))
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score)
      .map(({ action }) => action);
  }, [actions, query]);

  const grouped = useMemo(() => {
    const groupOrder: string[] = [];
    const map = new Map<string, CommandAction[]>();
    for (const a of filtered) {
      if (!map.has(a.group)) {
        groupOrder.push(a.group);
        map.set(a.group, []);
      }
      map.get(a.group)!.push(a);
    }
    return groupOrder.map((g) => ({ group: g, items: map.get(g)! }));
  }, [filtered]);

  const flatActions = useMemo(() => filtered, [filtered]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setSelectedIdx(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  useEffect(() => {
    if (flatActions.length === 0) {
      setSelectedIdx(0);
      return;
    }
    if (selectedIdx >= flatActions.length) {
      setSelectedIdx(0);
    }
  }, [flatActions.length, selectedIdx]);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-cmd-idx="${selectedIdx}"]`,
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [selectedIdx, open]);

  function activate(action: CommandAction) {
    onClose();
    requestAnimationFrame(() => action.onActivate());
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIdx((i) => (flatActions.length === 0 ? 0 : Math.min(i + 1, flatActions.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const action = flatActions[selectedIdx];
      if (action) activate(action);
    } else if (e.key === "Escape" && isTopModal(MODAL_ID)) {
      e.preventDefault();
      // Stop the native keydown event from bubbling further so that
      // any window-level Escape listeners on STACKED modals (e.g.,
      // SettingsPanel below us in the modal stack) don't also fire.
      // React's onKeyDown commits state synchronously, which triggers
      // useModalStack cleanup → popModal('command-palette') BEFORE the
      // native bubble reaches window — without this stop, the
      // bubbled Escape would see a popped stack and mistakenly close
      // the next modal too. ADR-095 Phase 2.4 stacked-modal QA finding.
      e.nativeEvent.stopImmediatePropagation();
      onClose();
    } else if (e.key === "Home") {
      e.preventDefault();
      setSelectedIdx(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setSelectedIdx(Math.max(flatActions.length - 1, 0));
    }
  }

  if (!rendered) return null;

  let runningIdx = -1;

  return (
    <>
      <div
        className={`${closing ? "bk-anim-backdrop-out" : "bk-anim-backdrop"} fixed inset-0 z-50 bg-[var(--scrim)]`}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-label="Command palette"
        aria-modal="true"
        className={`${closing ? "bk-anim-scale-out" : "bk-anim-fly-scale"} fixed left-1/2 top-[15vh] z-50 w-[min(640px,92vw)] -translate-x-1/2 overflow-hidden rounded-[var(--radius-xl)] bg-[var(--surface-raised)] shadow-[var(--elev-whisper-xl)] ring-1 ring-[var(--border-default)]`}
      >
        <div className="flex items-center gap-2 border-b border-[var(--border-default)] px-4 py-3">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            className="shrink-0 text-[var(--text-tertiary)]"
            aria-hidden="true"
          >
            <circle cx="7" cy="7" r="4.5" />
            <path d="M10.5 10.5L14 14" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            placeholder="Type a command or search…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            className="type-body flex-1 bg-transparent text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)]"
            aria-label="Command palette search"
            aria-autocomplete="list"
            aria-controls="cmd-palette-list"
            aria-activedescendant={
              flatActions[selectedIdx] ? `cmd-action-${flatActions[selectedIdx].id}` : undefined
            }
          />
        </div>
        <div id="cmd-palette-list" ref={listRef} className="max-h-96 overflow-y-auto py-1" role="listbox">
          {flatActions.length === 0 ? (
            <div className="type-body-sm px-4 py-6 text-center text-[var(--text-tertiary)]">
              No actions match{query ? ` "${query}"` : ""}
            </div>
          ) : (
            grouped.map(({ group, items }) => (
              <div key={group}>
                <div className="type-overline px-3 pb-1 pt-2.5 text-[var(--text-tertiary)]">
                  {group}
                </div>
                {items.map((a) => {
                  runningIdx += 1;
                  const idx = runningIdx;
                  const active = idx === selectedIdx;
                  return (
                    <button
                      key={a.id}
                      id={`cmd-action-${a.id}`}
                      data-cmd-idx={idx}
                      type="button"
                      role="option"
                      aria-selected={active}
                      onMouseEnter={() => setSelectedIdx(idx)}
                      onClick={() => activate(a)}
                      className={`type-body-sm flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] ${
                        active
                          ? "bg-[var(--accent-soft)] text-[var(--text-primary)]"
                          : "text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]"
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className={`truncate ${active ? "font-medium" : ""}`}>{a.label}</div>
                        {a.description && (
                          <div className="type-caption truncate text-[var(--text-tertiary)]">
                            {a.description}
                          </div>
                        )}
                      </div>
                      {a.shortcut && (
                        <kbd className="type-caption rounded-[var(--radius-sm)] bg-[var(--surface-sunken)] px-1.5 py-0.5 font-mono text-[var(--text-secondary)] shadow-[var(--elev-ring)]">
                          {a.shortcut}
                        </kbd>
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
        <div className="type-caption flex items-center justify-between border-t border-[var(--border-default)] bg-[var(--surface-sunken)] px-3 py-1.5 text-[var(--text-tertiary)]">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <kbd className="rounded-[var(--radius-sm)] bg-[var(--surface-raised)] px-1 font-mono shadow-[var(--elev-ring)]">
                ↑↓
              </kbd>
              navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="rounded-[var(--radius-sm)] bg-[var(--surface-raised)] px-1 font-mono shadow-[var(--elev-ring)]">
                ↵
              </kbd>
              select
            </span>
          </div>
          <span className="flex items-center gap-1">
            <kbd className="rounded-[var(--radius-sm)] bg-[var(--surface-raised)] px-1 font-mono shadow-[var(--elev-ring)]">
              esc
            </kbd>
            close
          </span>
        </div>
      </div>
    </>
  );
}
